import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Check, Send, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState({
    watchlist: [],
    auto_generate_signals: false,
    auto_train_models: false,
    target_percentage: 1.5,
    stop_loss_percentage: 0.8,
    max_hold_hours: 8,
    max_position_size_percent: 50,  // Max position size cap (% of capital)
    // Smart Select settings
    min_liquidity_24h: 25,  // Minimum 24h volume in millions ($25M default)
    // Backtest settings
    backtest_position_size: 1000,
    backtest_take_profit: 1.5,
    backtest_stop_loss: 0.8,
    backtest_max_hold_minutes: 30,
    backtest_transaction_cost: 0.1,
    backtest_use_hourly: false,
    // Telegram settings
    telegram_enabled: false,
    telegram_publish_buy: true,
    telegram_publish_sell: true,
    telegram_min_confidence: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [automationStatus, setAutomationStatus] = useState({
    is_running: false,
    started_at: null
  });
  const [startingAutomation, setStartingAutomation] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState({ configured: false });
  const [testingTelegram, setTestingTelegram] = useState(false);
  
  const availableCoins = [
    "BTC", "ETH", "SOL", "ADA", "XRP", 
    "THETA", "TFUEL", "DOT", "AVAX", "NMR", 
    "BNB", "LTC", "TRX", "BCH", "XMR", 
    "HBAR", "UNI", "LINK", "FET", "FIL",
    "SHIB", "DOGE", "LEO", "XLM",
    "SAND", "RENDER", "SUI", "DAI"
  ];
  
  useEffect(() => {
    loadSettings();
    loadAutomationStatus();
    loadTelegramStatus();
    
    // Poll automation status every 10 seconds
    const interval = setInterval(loadAutomationStatus, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const loadTelegramStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/telegram/status`);
      setTelegramStatus(response.data);
    } catch (error) {
      console.error('Failed to load Telegram status:', error);
    }
  };
  
  const testTelegramConnection = async () => {
    try {
      setTestingTelegram(true);
      const response = await axios.post(`${API}/settings/telegram/test`);
      
      if (response.data.status === 'success') {
        toast.success('Test message sent to Telegram channel!');
      } else {
        toast.error(response.data.message || 'Failed to send test message');
      }
    } catch (error) {
      console.error('Telegram test error:', error);
      toast.error('Failed to test Telegram connection');
    } finally {
      setTestingTelegram(false);
    }
  };
  
  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };
  
  const loadAutomationStatus = async () => {
    try {
      const response = await axios.get(`${API}/automation/status`);
      setAutomationStatus(response.data);
    } catch (error) {
      console.error('Failed to load automation status:', error);
    }
  };
  
  const startAutomation = async () => {
    if (settings.watchlist.length === 0) {
      toast.error('Please select at least one coin in your watchlist before starting');
      return;
    }
    
    try {
      setStartingAutomation(true);
      const response = await axios.post(`${API}/automation/start`);
      toast.success(`✅ ${response.data.message}`);
      await loadAutomationStatus();
    } catch (error) {
      console.error('Failed to start automation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start automation');
    } finally {
      setStartingAutomation(false);
    }
  };
  
  const stopAutomation = async () => {
    try {
      setStartingAutomation(true);
      const response = await axios.post(`${API}/automation/stop`);
      toast.success(`🛑 ${response.data.message}`);
      await loadAutomationStatus();
    } catch (error) {
      console.error('Failed to stop automation:', error);
      toast.error('Failed to stop automation');
    } finally {
      setStartingAutomation(false);
    }
  };
  
  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/settings`, settings);
      toast.success('✅ Settings saved successfully!');
      setSaving(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('❌ Failed to save settings');
      setSaving(false);
    }
  };
  
  const toggleCoin = (coin) => {
    setSettings(prev => ({
      ...prev,
      watchlist: prev.watchlist.includes(coin)
        ? prev.watchlist.filter(c => c !== coin)
        : [...prev.watchlist, coin]
    }));
  };
  
  const selectAll = () => {
    setSettings(prev => ({ ...prev, watchlist: availableCoins }));
  };
  
  const clearAll = () => {
    setSettings(prev => ({ ...prev, watchlist: [] }));
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <SettingsIcon className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold heading-font">Trading Settings</h1>
            <p className="text-sm text-muted-foreground">Configure your automated trading system</p>
          </div>
        </div>
        
        <div className="glass-card rounded-xl p-6 space-y-8">
          
          {/* Intraday Automation Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-primary">⚡</span> Intraday Signal Generation
              </h3>
              
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${automationStatus.is_running ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></div>
                <span className="text-sm font-medium">
                  {automationStatus.is_running ? 'Running' : 'Stopped'}
                </span>
              </div>
            </div>
            
            <div className="pl-7 space-y-4">
              {/* Auto-Generate Signals Toggle */}
              <div className="flex items-center justify-between p-4 bg-black/20 border border-white/10 rounded-lg">
                <div>
                  <label className="text-sm font-medium">Auto-Generate Signals (Cron/Script)</label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enable this to allow the automation script or cron job to generate signals automatically
                  </p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, auto_generate_signals: !prev.auto_generate_signals }))}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
                    settings.auto_generate_signals ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    settings.auto_generate_signals ? 'translate-x-8' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>
              
              {!settings.auto_generate_signals && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <p className="text-sm text-yellow-400">
                    ⚠️ Auto-generation is OFF. The automation script will skip signal generation until enabled.
                  </p>
                </div>
              )}
              {/* Status Info */}
              {automationStatus.is_running && automationStatus.started_at && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <p className="text-sm text-green-400">
                    ✅ Automation is running since {new Date(automationStatus.started_at).toLocaleString()}
                  </p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                    <li>Generating signals every 15 minutes for your watchlist</li>
                    <li>Monitoring positions every minute (target/stop loss tracking)</li>
                    <li>Evaluating signals at 15m, 1h, 4h milestones</li>
                  </ul>
                </div>
              )}
              
              {/* Control Buttons */}
              <div className="flex gap-3">
                {!automationStatus.is_running ? (
                  <button
                    onClick={startAutomation}
                    disabled={startingAutomation || settings.watchlist.length === 0}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {startingAutomation ? 'Starting...' : 'Start Intraday Signal Generation'}
                  </button>
                ) : (
                  <button
                    onClick={stopAutomation}
                    disabled={startingAutomation}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    {startingAutomation ? 'Stopping...' : 'Stop Intraday Signal Generation'}
                  </button>
                )}
              </div>
              
              {/* Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-400 mb-2">How It Works:</h4>
                <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">
                  <li>Configure your watchlist below (select coins you want to trade)</li>
                  <li>Set risk management parameters (target %, stop loss %, max hold time)</li>
                  <li>Click "Start Intraday Signal Generation" to begin automation</li>
                  <li>System will automatically generate signals, monitor positions, and evaluate performance</li>
                  <li>Manually retrain models using "Train Models" button in Dashboard whenever needed</li>
                  <li>Click "Stop" anytime to pause automation</li>
                  <li>Use "Close Auto Positions" button on Dashboard to force-close all positions</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Risk Management Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-primary">🎯</span> Risk Management (Day Trading)
            </h3>
            
            <div className="grid grid-cols-3 gap-4 pl-7">
              <div>
                <label className="block text-sm font-medium mb-2">Target % (Take Profit)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={settings.target_percentage}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    target_percentage: parseFloat(e.target.value) || 1.5
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 1.5% (aligned with daily ML models)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Stop Loss %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="5"
                  value={settings.stop_loss_percentage}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    stop_loss_percentage: parseFloat(e.target.value) || 0.8
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 0.8% (R:R ratio 1.875:1)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Signal Frequency (minutes)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  step="1"
                  value={settings.signal_frequency_minutes || 5}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    signal_frequency_minutes: parseInt(e.target.value) || 5
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 15 min (aligned with ML model)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Max Hold (minutes)</label>
                <input
                  type="number"
                  min="5"
                  max="240"
                  step="5"
                  value={settings.max_hold_minutes || 10}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    max_hold_minutes: parseInt(e.target.value) || 10
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 60 min (aligned with ML model)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Initial Capital ($)</label>
                <input
                  type="number"
                  min="100"
                  step="100"
                  value={settings.initial_capital || 1500}
                  onChange={(e) => {
                    const capital = parseFloat(e.target.value) || 1500;
                    const maxPos = settings.max_positions || 3;
                    setSettings(prev => ({ 
                      ...prev, 
                      initial_capital: capital,
                      position_size: capital / maxPos
                    }));
                  }}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Your total trading capital</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Max Concurrent Positions</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.max_positions || 3}
                  onChange={(e) => {
                    const maxPos = parseInt(e.target.value) || 3;
                    const capital = settings.initial_capital || 1500;
                    setSettings(prev => ({ 
                      ...prev, 
                      max_positions: maxPos,
                      position_size: capital / maxPos
                    }));
                  }}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Position size: ${((settings.initial_capital || 1500) / (settings.max_positions || 3)).toFixed(2)} each
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Max Position Size (%)</label>
                <input
                  type="number"
                  min="10"
                  max="100"
                  step="5"
                  value={settings.max_position_size_percent || 50}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    max_position_size_percent: parseInt(e.target.value) || 50
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Max ${((automationStatus?.capital?.effective || settings.initial_capital || 1500) * (settings.max_position_size_percent || 50) / 100).toFixed(2)} per position (caps concentration risk)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Min Liquidity - Smart Select ($M)</label>
                <input
                  type="number"
                  min="10"
                  max="100"
                  step="5"
                  value={settings.min_liquidity_24h || 25}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    min_liquidity_24h: parseInt(e.target.value) || 25
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Coins with 24h volume below ${settings.min_liquidity_24h || 25}M excluded from Smart Select
                </p>
              </div>
            </div>
            
            <div className="pl-7 text-sm text-muted-foreground bg-primary/5 p-3 rounded-lg">
              <strong>Risk/Reward Ratio:</strong> {(settings.target_percentage / settings.stop_loss_percentage).toFixed(2)}:1
              {(settings.target_percentage / settings.stop_loss_percentage) >= 1.5 && 
                <span className="text-green-500 ml-2">✓ Good ratio for day trading</span>
              }
            </div>
          </div>
          
          {/* Manual Trading Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-primary">🎯</span> Manual Trading Settings
            </h3>
            
            <div className="grid grid-cols-2 gap-4 pl-7">
              <div>
                <label className="block text-sm font-medium mb-2">Manual Trading Capital ($)</label>
                <input
                  type="number"
                  value={settings.manual_position_size || 500}
                  onChange={(e) => {
                    const size = parseFloat(e.target.value) || 500;
                    setSettings({
                      ...settings,
                      manual_position_size: size
                    });
                  }}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Total capital for manual trading (divided by number of coins selected)
                </p>
              </div>
            </div>
          </div>
          
          {/* Backtest Settings Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-primary">📊</span> Backtest Settings
            </h3>
            <p className="text-sm text-muted-foreground pl-7">
              Configure parameters for backtesting ML models. These settings simulate how trades would perform historically.
            </p>
            
            <div className="grid grid-cols-3 gap-4 pl-7">
              <div>
                <label className="block text-sm font-medium mb-2">Position Size ($)</label>
                <input
                  type="number"
                  step="100"
                  min="100"
                  value={settings.backtest_position_size || 1000}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_position_size: parseFloat(e.target.value) || 1000
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Amount per trade in backtest</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Take Profit %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="20"
                  value={settings.backtest_take_profit || 1.5}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_take_profit: parseFloat(e.target.value) || 1.5
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Exit when profit reaches this %</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Stop Loss %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={settings.backtest_stop_loss || 0.8}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_stop_loss: parseFloat(e.target.value) || 0.8
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Exit when loss reaches this %</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Max Hold (Minutes)</label>
                <input
                  type="number"
                  step="1"
                  min="5"
                  max="10080"
                  value={settings.backtest_max_hold_minutes || 30}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_max_hold_minutes: parseInt(e.target.value) || 30
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Exit after this time if no TP/SL hit (e.g., 30 = 30 mins)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Transaction Cost %</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={settings.backtest_transaction_cost || 0.1}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_transaction_cost: parseFloat(e.target.value) || 0.1
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">Fee per trade (applied twice: entry + exit)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Data Granularity</label>
                <select
                  value={settings.backtest_use_hourly ? "hourly" : "daily"}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    backtest_use_hourly: e.target.value === "hourly"
                  }))}
                  className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="daily">Daily (up to 90 days)</option>
                  <option value="hourly">Hourly (limited to 30 days)</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">Hourly = more trades, Daily = longer history</p>
              </div>
            </div>
            
            {/* Backtest Risk/Reward Summary */}
            <div className="pl-7 grid grid-cols-2 gap-4">
              <div className="bg-primary/5 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Risk/Reward Ratio:</strong>{' '}
                  <span className={`font-bold ${(settings.backtest_take_profit || 1.5) / (settings.backtest_stop_loss || 0.8) >= 1.5 ? 'text-green-500' : 'text-yellow-500'}`}>
                    {((settings.backtest_take_profit || 1.5) / (settings.backtest_stop_loss || 0.8)).toFixed(2)}:1
                  </span>
                  {(settings.backtest_take_profit || 1.5) / (settings.backtest_stop_loss || 0.8) >= 1.5 && 
                    <span className="text-green-500 ml-2">✓ Good</span>
                  }
                </p>
              </div>
              <div className="bg-primary/5 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Break-even Win Rate:</strong>{' '}
                  <span className="font-bold text-blue-400">
                    {(100 / (1 + (settings.backtest_take_profit || 1.5) / (settings.backtest_stop_loss || 0.8))).toFixed(1)}%
                  </span>
                  <span className="text-xs ml-2">(min required to profit)</span>
                </p>
              </div>
            </div>
            
            {/* Info Box */}
            <div className="pl-7 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-400 mb-2">💡 Backtest Tips</h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li><strong>TP/SL Simulation:</strong> Uses intraday high/low prices to check if TP or SL would be hit</li>
                <li><strong>Transaction Costs:</strong> Total cost = 2× this value (entry + exit). Binance ~0.1%, Coinbase ~0.5%</li>
                <li><strong>Daily Data:</strong> Better for swing trading backtests (longer periods)</li>
                <li><strong>Hourly Data:</strong> Better for day trading backtests (more precise but limited to ~30 days)</li>
                <li><strong>Risk Metrics:</strong> Backtest now includes Sharpe Ratio, Max Drawdown, and Win/Loss Streaks</li>
              </ul>
            </div>
          </div>
          
          {/* Watchlist Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-primary">📊</span> My Watchlist 
                <span className="text-sm font-normal text-muted-foreground">
                  ({settings.watchlist.length} of {availableCoins.length} coins)
                </span>
              </h3>
              <div className="flex gap-2">
                <button 
                  onClick={selectAll} 
                  className="px-3 py-1.5 text-sm bg-primary/20 hover:bg-primary/30 rounded-lg transition-colors"
                >
                  Select All
                </button>
                <button 
                  onClick={clearAll} 
                  className="px-3 py-1.5 text-sm bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-6 gap-3 pl-7">
              {availableCoins.map(coin => {
                const isSelected = settings.watchlist.includes(coin);
                return (
                  <label
                    key={coin}
                    className={`
                      relative flex items-center justify-center gap-2 px-4 py-3 rounded-lg cursor-pointer
                      transition-all border-2
                      ${isSelected
                        ? 'bg-primary/20 border-primary shadow-lg shadow-primary/20'
                        : 'bg-black/20 border-white/10 hover:bg-black/30 hover:border-white/20'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleCoin(coin)}
                      className="hidden"
                    />
                    <span className="font-semibold">{coin}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary absolute top-1 right-1" />
                    )}
                  </label>
                );
              })}
            </div>
            
            {settings.watchlist.length === 0 && (
              <div className="text-center py-8 text-muted-foreground bg-yellow-500/10 rounded-lg border border-yellow-500/20 pl-7">
                <p className="text-yellow-500 font-medium">⚠️ No coins selected</p>
                <p className="text-sm mt-1">Select at least one coin for automated signal generation</p>
              </div>
            )}
            
            {settings.watchlist.length > 10 && (
              <div className="text-center py-4 text-muted-foreground bg-blue-500/10 rounded-lg border border-blue-500/20 pl-7">
                <p className="text-sm">
                  💡 Tip: For optimal performance, consider selecting 5-10 coins for focused trading
                </p>
              </div>
            )}
          </div>
          
          {/* Telegram Signal Publisher Section */}
          <div className="space-y-4" data-testid="telegram-settings-section">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-400" />
              <span>Telegram Signal Publisher</span>
              {telegramStatus.configured && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  Configured
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground pl-7">
              Automatically publish executed trade signals to your private Telegram channel.
            </p>
            
            <div className="pl-7 space-y-4">
              {/* Master Toggle */}
              <div className="flex items-center justify-between p-4 bg-black/20 border border-white/10 rounded-lg">
                <div>
                  <label className="text-sm font-medium">Enable Telegram Publishing</label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Master switch to enable/disable all Telegram notifications
                  </p>
                </div>
                <button
                  data-testid="telegram-enabled-toggle"
                  onClick={() => setSettings(prev => ({ ...prev, telegram_enabled: !prev.telegram_enabled }))}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
                    settings.telegram_enabled ? 'bg-blue-600' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    settings.telegram_enabled ? 'translate-x-8' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>
              
              {/* Conditional Settings (only show when enabled) */}
              {settings.telegram_enabled && (
                <>
                  {/* Signal Type Toggles */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-black/20 border border-white/10 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-green-400">Publish BUY Signals</label>
                        <p className="text-xs text-muted-foreground mt-1">Long position entries</p>
                      </div>
                      <button
                        data-testid="telegram-publish-buy-toggle"
                        onClick={() => setSettings(prev => ({ ...prev, telegram_publish_buy: !prev.telegram_publish_buy }))}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                          settings.telegram_publish_buy ? 'bg-green-600' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                          settings.telegram_publish_buy ? 'translate-x-6' : 'translate-x-0.5'
                        }`}></div>
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-black/20 border border-white/10 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-red-400">Publish SELL Signals</label>
                        <p className="text-xs text-muted-foreground mt-1">Short position entries</p>
                      </div>
                      <button
                        data-testid="telegram-publish-sell-toggle"
                        onClick={() => setSettings(prev => ({ ...prev, telegram_publish_sell: !prev.telegram_publish_sell }))}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                          settings.telegram_publish_sell ? 'bg-red-600' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                          settings.telegram_publish_sell ? 'translate-x-6' : 'translate-x-0.5'
                        }`}></div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Minimum Confidence */}
                  <div className="p-4 bg-black/20 border border-white/10 rounded-lg">
                    <label className="block text-sm font-medium mb-2">Minimum Confidence (%)</label>
                    <input
                      data-testid="telegram-min-confidence-input"
                      type="number"
                      min="0"
                      max="100"
                      step="5"
                      value={settings.telegram_min_confidence || 0}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        telegram_min_confidence: parseInt(e.target.value) || 0
                      }))}
                      className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Only publish signals with confidence at or above this threshold (0 = all signals)
                    </p>
                  </div>
                  
                  {/* Test Connection Button */}
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="telegram-test-btn"
                      onClick={testTelegramConnection}
                      disabled={testingTelegram || !telegramStatus.configured}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                    >
                      <Send className="w-4 h-4" />
                      {testingTelegram ? 'Sending...' : 'Send Test Message'}
                    </button>
                    
                    {!telegramStatus.configured && (
                      <span className="text-xs text-yellow-400">
                        Bot not configured. Set TELEGRAM_BOT_TOKEN and TELEGRAM_CHANNEL_ID in backend .env
                      </span>
                    )}
                  </div>
                </>
              )}
              
              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-400 mb-2">How It Works</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>When a new position is opened, a formatted message is sent to your Telegram channel</li>
                  <li>Messages include: coin, signal type, entry price, targets, stop loss, and expiry time</li>
                  <li>The publisher runs as a separate background service watching the database</li>
                  <li>Already published signals are tracked to prevent duplicates</li>
                  <li>Enable/disable publishing using the toggle above - the service checks this setting</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="pt-6 border-t border-white/10 flex justify-end gap-3">
            <button
              onClick={loadSettings}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg font-semibold transition-colors"
            >
              Reset Changes
            </button>
            <button
              onClick={saveSettings}
              disabled={saving || settings.watchlist.length === 0}
              className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
          
        </div>
        
      </div>
    </div>
  );
};

export default Settings;
